<template>
	<div class="p-4 md:p-6 flex-1 overflow-y-auto font-sans">
		<!-- ── Section Header ── -->
		<div
			class="flex items-center justify-between mb-6 pb-5 border-b border-primary-100 dark:border-dark-600">
			<div class="flex items-center gap-3">
				<div
					class="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-400/15 flex items-center justify-center flex-shrink-0">
					<svg
						class="w-5 h-5 text-primary-400"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24">
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
					</svg>
				</div>
				<div>
					<h1
						class="font-display text-xl md:text-2xl font-extrabold tracking-tight text-dark-700 dark:text-white">
						Event Management
					</h1>
					<p class="text-[13px] text-muted dark:text-muted-light mt-0.5">
						Create and publish a new event
					</p>
				</div>
			</div>
		</div>

		<Breadcrumbs />

		<form @submit.prevent="submitForm" class="space-y-4 mt-2">
			<!-- ══════════════════════════════════════════
           CARD 1 — Image + Title + Location
      ══════════════════════════════════════════ -->
			<div
				class="bg-surface dark:bg-dark-700 rounded-card shadow-card border border-primary-100 dark:border-dark-600 p-5 md:p-6">
				<!-- Card label -->
				<p
					class="text-[11px] font-bold uppercase tracking-widest text-muted dark:text-muted-light mb-5">
					Basic Info
				</p>

				<div class="flex flex-col md:flex-row gap-6">
					<!-- Image Uploader -->
					<div
						class="flex flex-col justify-center items-center rounded-xl w-full md:w-64 h-52 border-2 border-dashed cursor-pointer transition-all duration-200 border-primary-200 dark:border-dark-500 hover:border-primary-400 dark:hover:border-primary-400 bg-primary-50/50 dark:bg-dark-600/50"
						@dragover.prevent
						@drop.prevent="handleDrop">
						<template v-if="!event.imageURL">
							<div
								class="w-12 h-12 rounded-xl bg-primary-100 dark:bg-dark-500 flex items-center justify-center mb-3">
								<svg
									class="w-6 h-6 text-primary-400"
									fill="none"
									stroke="currentColor"
									stroke-width="2"
									viewBox="0 0 24 24">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1M16 12l-4-4m0 0l-4 4m4-4v12" />
								</svg>
							</div>
							<p
								class="text-[13px] text-muted dark:text-muted-light text-center leading-relaxed">
								Drop image to upload<br />or
								<span
									class="text-primary-400 font-semibold cursor-pointer hover:text-primary-600 transition-colors"
									@click="browseFiles"
									>browse</span
								>
							</p>
						</template>

						<!-- Preview -->
						<template v-else>
							<img
								:src="event.imageURL"
								alt="Preview"
								class="w-full h-40 object-cover rounded-lg" />
							<button
								@click="resetImage"
								class="mt-2 text-[12px] font-semibold text-red-400 hover:text-red-600 transition-colors">
								Remove
							</button>
						</template>

						<input
							type="file"
							ref="fileInput"
							class="hidden"
							@change="handleImageUpload" />

							<ImageCropperModal
								:open="cropperOpen"
								:src="cropperSrc"
								:file-name="cropperFileName"
								:aspect="16 / 14"
								@cancel="closeCropper"
								@done="onCropDone" />
					</div>

					<!-- Title + Location -->
					<div class="flex-1 flex flex-col gap-4">
						<!-- Event Title -->
						<div>
							<label class="form-label">
								<svg
									class="w-4 h-4"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M7 7h10M7 11h7M7 15h4" />
								</svg>
								Event Title
							</label>
							<input
								v-model="event.title"
								type="text"
								class="form-input"
								:class="{
									'border-red-400 focus:ring-red-400/30': errors.title,
									'border-primary-400 focus:ring-primary-400/20':
										!errors.title && event.title,
								}" />
							<ErrorMessage :message="errors.title" v-if="errors.title" />
						</div>

						<!-- Event Location -->
						<div>
							<label class="form-label">
								<svg
									class="w-4 h-4"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
								</svg>
								Event Location
							</label>
							<input
								v-model="locationQuery"
								@input="fetchLocationSuggestions"
								type="text"
								class="form-input" />

							<!-- Suggestions -->
							<ul
								v-if="locationSuggestions.length"
								class="mt-1.5 rounded-xl border border-primary-100 dark:border-dark-500 bg-surface dark:bg-dark-700 shadow-dropdown overflow-hidden max-h-40 overflow-y-auto">
								<li
									v-for="(suggestion, index) in locationSuggestions"
									:key="index"
									@click="selectLocation(suggestion)"
									class="px-3.5 py-2.5 text-[13px] text-dark-700 dark:text-white/80 cursor-pointer hover:bg-primary-50 dark:hover:bg-dark-600 transition-colors border-b border-primary-50 dark:border-dark-600 last:border-0">
									{{ suggestion.display_name }}
								</li>
							</ul>

							<!-- Map -->
							<LMap
								v-if="event.lat && event.lng"
								ref="mapRef"
								:zoom="zoom"
								:center="[event.lat, event.lng]"
								class="w-full rounded-xl border border-primary-100 dark:border-dark-500 mt-1"
								:use-global-leaflet="false"
								@click="setLocation">
								<LTileLayer
									url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
								<LMarker
									v-if="event.lat && event.lng"
									:lat-lng="[event.lat, event.lng]" />
							</LMap>

							<ErrorMessage :message="errors.location" v-if="errors.location" />
						</div>
					</div>
				</div>
			</div>

			<!-- ── Section divider ── -->
			<div class="form-divider" />

			<!-- ══════════════════════════════════════════
           CARD 2 — Date & Time
      ══════════════════════════════════════════ -->
			<div
				class="bg-surface dark:bg-dark-700 rounded-card shadow-card border border-primary-100 dark:border-dark-600 p-5 md:p-6">
				<p
					class="text-[11px] font-bold uppercase tracking-widest text-muted dark:text-muted-light mb-5">
					Date & Time
				</p>

				<!-- Row 1: Dates -->
				<div class="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
					<div>
						<label class="form-label">
							<svg
								class="w-4 h-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
							</svg>
							Start Date
						</label>
						<input
							v-model="event.date"
							type="date"
							class="form-input"
							:class="{ 'border-red-400': errors.date }" />
						<ErrorMessage :message="errors.date" v-if="errors.date" />
					</div>
					<div>
						<label class="form-label">
							<svg
								class="w-4 h-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
							</svg>
							End Date
						</label>
						<input
							v-model="event.endDate"
							type="date"
							class="form-input"
							:class="{ 'border-red-400': errors.endDate }" />
						<ErrorMessage :message="errors.endDate" v-if="errors.endDate" />
					</div>
				</div>

				<!-- Row 2: Times -->
				<div class="grid grid-cols-1 md:grid-cols-2 gap-5">
					<div>
						<label class="form-label">
							<svg
								class="w-4 h-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
							</svg>
							Start Time
						</label>
						<input
							v-model="event.startTime"
							type="time"
							class="form-input"
							:class="{ 'border-red-400': errors.startTime }" />
						<ErrorMessage :message="errors.startTime" v-if="errors.startTime" />
					</div>
					<div>
						<label class="form-label">
							<svg
								class="w-4 h-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
							</svg>
							End Time
						</label>
						<input
							v-model="event.endTime"
							type="time"
							class="form-input"
							:class="{ 'border-red-400': errors.endTime }" />
						<ErrorMessage :message="errors.endTime" v-if="errors.endTime" />
					</div>
				</div>
			</div>

			<!-- ── Section divider ── -->
			<div class="form-divider" />

			<!-- ══════════════════════════════════════════
           CARD 3 — Pricing + Capacity + Organizer
      ══════════════════════════════════════════ -->
			<div
				class="bg-surface dark:bg-dark-700 rounded-card shadow-card border border-primary-100 dark:border-dark-600 p-5 md:p-6">
				<div class="flex items-center justify-between mb-5">
					<p
						class="text-[11px] font-bold uppercase tracking-widest text-muted dark:text-muted-light">
						Pricing & Capacity
					</p>
					<!-- Stripe status badge -->
					<div
						class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold"
						:class="
							stripeConnected
								? 'bg-primary-50 dark:bg-primary-400/15 text-primary-600 dark:text-primary-300'
								: 'bg-red-50 dark:bg-red-950/30 text-red-500'
						">
						<svg
							class="w-3 h-3"
							viewBox="0 0 24 24"
							fill="none">
							<circle cx="12" cy="12" r="10" fill="currentColor" />
							<path
								v-if="stripeConnected"
								d="M7 12l4 4 6-7"
								stroke="white"
								stroke-width="2.5"
								stroke-linecap="round" />
							<path
								v-else
								d="M8 8l8 8M16 8l-8 8"
								stroke="white"
								stroke-width="2.5"
								stroke-linecap="round" />
						</svg>
						{{ stripeConnected ? 'Payments Ready' : 'Payments Not Set Up' }}
					</div>
				</div>

				<!-- Price rows -->
				<div class="flex flex-col gap-3 mb-4">
					<div
						v-for="(price, index) in event.prices"
						:key="index"
						class="flex flex-col md:flex-row items-stretch md:items-center gap-3 p-3.5 rounded-xl bg-primary-50/60 dark:bg-dark-600/60 border border-primary-100 dark:border-dark-500">
						<!-- Type -->
						<div class="flex-1">
							<input
								v-model="price.type"
								type="text"
								placeholder="Price type (e.g. VIP, Regular)"
								class="form-input !py-2"
								:class="{
									'border-red-400':
										errors.price && (!price.type || !price.amount),
								}" />
						</div>
						<!-- Amount — gated by Stripe status -->
						<div class="flex-1 relative">
							<input
								:value="price.amount"
								@input="handlePriceAmountInput(price, $event.target.value)"
								type="number"
								step="0.01"
								min="0"
								placeholder="Amount (€)"
								class="form-input !py-2"
								:class="{
									'border-red-400':
										errors.price && (!price.type || !price.amount),
								}" />
							<!-- lock icon overlay when stripe not connected -->
							<div
								v-if="!stripeConnected"
								class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
								<svg
									class="w-4 h-4 text-muted dark:text-muted-light opacity-60"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
								</svg>
							</div>
						</div>
						<!-- Remove -->
						<button
							type="button"
							@click="removePrice(index)"
							class="flex items-center justify-center w-9 h-9 rounded-xl flex-shrink-0 bg-red-50 dark:bg-red-950/30 text-red-400 hover:bg-red-100 dark:hover:bg-red-950/50 transition-colors">
							<i class="pi pi-trash text-[14px]" />
						</button>
					</div>
				</div>

				<ErrorMessage :message="errors.price" v-if="errors.price" />

				<!-- Add price -->
				<button
					type="button"
					@click="addPrice"
					class="flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-semibold border-2 border-dashed border-primary-300 dark:border-primary-400/40 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-400/10 hover:border-primary-400 transition-all duration-200 mb-1">
					<svg
						class="w-4 h-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24">
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2.5"
							d="M12 4v16m8-8H4" />
					</svg>
					Add New Price
				</button>
				<p class="text-[11px] text-muted dark:text-muted-light mb-6">
					You can add multiple ticket types (e.g. Regular, VIP, Early Bird).
				</p>

				<!-- Capacity + Organizer -->
				<div class="grid grid-cols-1 md:grid-cols-2 gap-5">
					<div>
						<label class="form-label">
							<svg
								class="w-4 h-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
							</svg>
							Expected Attendees
						</label>
						<input
							v-model="event.capacity"
							type="number"
							class="form-input"
							:class="{ 'border-red-400': errors.capacity }" />
						<ErrorMessage :message="errors.capacity" v-if="errors.capacity" />
					</div>

					<div>
						<label class="form-label">
							<svg
								class="w-4 h-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
							</svg>
							Event Organizer
						</label>
						<select
							v-model="event.organizer"
							v-if="forAdmin()"
							class="form-input"
							:class="{
								'border-red-400': errors.organizer,
								'border-primary-400': !errors.organizer && organizations,
							}">
							<option value="" disabled>Select an Organizer</option>
							<option
								v-for="org in organizations"
								:key="org.ID"
								:value="String(org.ID)">
								{{ org.Name }}
							</option>
						</select>
						<input
							v-if="forClient()"
							type="text"
							:value="userInfo.name"
							disabled
							class="form-input opacity-60 cursor-not-allowed bg-primary-50 dark:bg-dark-600" />
						<input v-if="forClient()" type="hidden" v-model="event.organizer" />
						<ErrorMessage :message="errors.organizer" v-if="errors.organizer" />
					</div>
				</div>
			</div>

			<!-- ── Section divider ── -->
			<div class="form-divider" />

			<!-- ══════════════════════════════════════════
           CARD 4 — Categories + Description
      ══════════════════════════════════════════ -->
			<div
				class="bg-surface dark:bg-dark-700 rounded-card shadow-card border border-primary-100 dark:border-dark-600 p-5 md:p-6">
				<p
					class="text-[11px] font-bold uppercase tracking-widest text-muted dark:text-muted-light mb-5">
					Categories & Description
				</p>

				<!-- Categories -->
				<div class="mb-6">
					<label class="form-label">
						<svg
							class="w-4 h-4"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
						</svg>
						Choose Category
						<span
							class="ml-auto text-[10px] font-bold text-muted dark:text-muted-light">
							max 3
						</span>
					</label>

					<select
						v-model="selectedCategory"
						@change="addCategory"
						:disabled="event.selectedCategories.length >= 3"
						class="form-input"
						:class="{
							'border-red-400': errors.categories,
							'border-primary-400': !errors.categories && categories,
							'opacity-50 cursor-not-allowed':
								event.selectedCategories.length >= 3,
						}">
						<option value="" disabled>Select a category</option>
						<option
							v-for="category in categories"
							:key="category.id"
							:value="category.id.toString()">
							{{ category.name }}
						</option>
					</select>

					<!-- Selected tags -->
					<div class="flex flex-wrap gap-2 mt-3">
						<span
							v-for="categoryId in event.selectedCategories"
							:key="categoryId"
							class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold bg-primary-400 text-white">
							{{ getCategoryName(categoryId) }}
							<button
								@click="removeCategory(categoryId)"
								class="w-4 h-4 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/40 transition-colors text-white leading-none">
								&times;
							</button>
						</span>
					</div>

					<p
						v-if="event.selectedCategories.length >= 3"
						class="mt-2 text-[12px] text-muted dark:text-muted-light bg-primary-50 dark:bg-dark-600 rounded-lg px-3 py-2 text-center">
						Maximum 3 categories selected
					</p>
					<ErrorMessage :message="errors.categories" v-if="errors.categories" />
				</div>

				<!-- Description -->
				<div>
					<label class="form-label">
						<svg
							class="w-4 h-4"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M4 6h16M4 10h16M4 14h10" />
						</svg>
						Reach out to your Audience
					</label>

					<textarea
						v-model="event.description"
						rows="1"
						style="
							overflow-y: auto;
							resize: none;
							max-height: 400px;
							min-height: 100px;
						"
						@input="handleTextarea"
						class="form-input"
						:class="[
							isValid
								? 'border-primary-400 focus:ring-primary-400/20'
								: 'border-red-400 focus:ring-red-400/20',
						]" />

					<ErrorMessage
						:message="descriptionError.description"
						v-if="descriptionError.description" />

					<!-- Char counter -->
					<div class="mt-2 flex items-center justify-between">
						<div class="text-[12px]">
							<span
								:class="[
									charCount > maxChars
										? 'text-red-500'
										: charCount < minChars
										? 'text-accent-600 dark:text-accent-400'
										: 'text-primary-600 dark:text-primary-400',
									'font-semibold',
								]"
								>{{ charCount }}</span
							>
							<span class="text-muted dark:text-muted-light">
								/ {{ maxChars }} characters</span
							>
						</div>
						<div class="text-[11px]">
							<span
								v-if="charCount < minChars"
								class="text-accent-600 dark:text-accent-400">
								{{ minimumChar }}
							</span>
							<span v-if="charCount > maxChars" class="text-red-500">
								{{ exceedMaxChar }}
							</span>
						</div>
					</div>

					<!-- Progress bar -->
					<div
						class="mt-1.5 h-1 rounded-full bg-primary-100 dark:bg-dark-500 overflow-hidden">
						<div
							class="h-full rounded-full transition-all duration-300"
							:class="[
								charCount > maxChars
									? 'bg-red-400'
									: charCount < minChars
									? 'bg-accent-400'
									: 'bg-primary-400',
							]"
							:style="{
								width: Math.min((charCount / maxChars) * 100, 100) + '%',
							}" />
					</div>
				</div>
			</div>

			<!-- ── Section divider ── -->
			<div class="form-divider" />

			<!-- ══════════════════════════════════════════
           Submit
      ══════════════════════════════════════════ -->
			<button
				type="submit"
				:disabled="isSubmitting || charCount < minChars || charCount > maxChars"
				class="w-full font-display font-bold text-[14px] py-3.5 rounded-xl transition-all duration-200 bg-dark-700 dark:bg-primary-400 text-white hover:bg-primary-600 dark:hover:bg-primary-600 hover:-translate-y-0.5 shadow-card hover:shadow-teal disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none">
				<span v-if="!isLoading" class="flex items-center justify-center gap-2">
					<svg
						class="w-4 h-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24">
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2.5"
							d="M5 13l4 4L19 7" />
					</svg>
					Save Event
				</span>
				<span v-else class="flex items-center justify-center gap-2">
					<svg
						class="animate-spin w-4 h-4 text-white"
						fill="none"
						viewBox="0 0 24 24">
						<circle
							class="opacity-25"
							cx="12"
							cy="12"
							r="10"
							stroke="currentColor"
							stroke-width="4" />
						<path
							class="opacity-75"
							fill="currentColor"
							d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962
                     7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
					</svg>
					Processing...
				</span>
			</button>
		</form>
	</div>

	<!-- ══ PAYMENT ACCOUNT REQUIRED MODAL ══ -->
	<transition name="modal">
		<div
			v-if="showStripeModal"
			class="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4"
			@click.self="resetPriceAmounts">
			<div
				class="bg-surface dark:bg-dark-700 w-full max-w-md rounded-card shadow-dropdown border border-primary-100 dark:border-dark-600 overflow-hidden">

				<!-- top accent bar -->
				<div class="h-1.5 w-full bg-gradient-to-r from-red-400 to-accent-400" />

				<div class="p-6">
					<!-- icon -->
					<div
						class="w-12 h-12 rounded-xl bg-red-50 dark:bg-red-950/30 flex items-center justify-center mb-4">
						<svg
							class="w-6 h-6 text-red-400"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
						</svg>
					</div>

					<!-- heading -->
					<p
						class="text-[10px] font-bold uppercase tracking-widest text-red-400 mb-1">
						Payment Account Required
					</p>
					<h2
						class="font-display text-[17px] font-bold text-dark-700 dark:text-white mb-2">
						Payment Account Not Set Up
					</h2>
					<p
						class="text-[13px] text-muted dark:text-muted-light leading-relaxed mb-5">
						You need to set up your payment account before you can charge for
						tickets. Head to
						<strong class="text-dark-700 dark:text-white">Revenue</strong> to
						complete the setup — it only takes a minute.
					</p>

					<!-- hint chip -->
					<div
						class="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-primary-50 dark:bg-dark-600 border border-primary-100 dark:border-dark-500 mb-5">
						<svg
							class="w-4 h-4 text-primary-400 flex-shrink-0"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 110 20A10 10 0 0112 2z" />
						</svg>
						<p class="text-[12px] text-muted dark:text-muted-light">
							You can still create a
							<span
								class="font-semibold text-primary-600 dark:text-primary-400"
								>free event</span
							>
							(amount = 0) without a payment account.
						</p>
					</div>

					<!-- actions -->
					<div class="flex gap-3">
						<button
							@click="resetPriceAmounts"
							class="flex-1 px-4 py-2.5 rounded-xl text-[13px] font-semibold bg-primary-50 dark:bg-dark-600 text-dark-400 dark:text-white/70 border border-primary-100 dark:border-dark-500 hover:bg-primary-100 dark:hover:bg-dark-500 transition-colors">
							Stay Here
						</button>
						<router-link
							to="/admin/revenue"
							class="flex-1 px-4 py-2.5 rounded-xl text-[13px] font-bold text-center bg-dark-700 dark:bg-primary-400 text-white hover:bg-primary-600 dark:hover:bg-primary-600 hover:-translate-y-0.5 shadow-card transition-all duration-200">
							Set Up Payments →
						</router-link>
					</div>
				</div>
			</div>
		</div>
	</transition>

	<!-- ══ EVENT VISIBILITY (PUBLIC / PRIVATE) MODAL ══ -->
	<transition name="modal">
		<div
			v-if="showVisibilityModal"
			class="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4"
			@click.self="closeVisibilityModal">
			<div
				class="bg-surface dark:bg-dark-700 w-full max-w-md rounded-card shadow-dropdown border border-primary-100 dark:border-dark-600 overflow-hidden">

				<!-- top accent bar -->
				<div class="h-1.5 w-full bg-gradient-to-r from-primary-400 to-accent-400" />

				<div class="p-6">
					<!-- icon -->
					<div
						class="w-12 h-12 rounded-xl bg-primary-50 dark:bg-primary-400/15 flex items-center justify-center mb-4">
						<svg
							class="w-6 h-6 text-primary-400"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
						</svg>
					</div>

					<!-- heading -->
					<p
						class="text-[10px] font-bold uppercase tracking-widest text-primary-400 mb-1">
						Last Step
					</p>
					<h2
						class="font-display text-[17px] font-bold text-dark-700 dark:text-white mb-2">
						Who can see this event?
					</h2>
					<p
						class="text-[13px] text-muted dark:text-muted-light leading-relaxed mb-5">
						Choose whether this event is open to everyone or requires your
						approval for each attendee.
					</p>

					<!-- Options -->
					<div class="flex flex-col gap-3 mb-5">
						<!-- Public option -->
						<button
							type="button"
							@click="event.is_private = false"
							class="text-left p-4 rounded-xl border-2 transition-all duration-200"
							:class="
								!event.is_private
									? 'border-primary-400 bg-primary-50 dark:bg-primary-400/10'
									: 'border-primary-100 dark:border-dark-500 bg-primary-50/40 dark:bg-dark-600/40 hover:border-primary-300'
							">
							<div class="flex items-start gap-3">
								<div
									class="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
									:class="
										!event.is_private
											? 'bg-primary-400 text-white'
											: 'bg-primary-100 dark:bg-dark-500 text-primary-400'
									">
									<svg
										class="w-4.5 h-4.5"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24">
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M3 12h18M12 3a15.3 15.3 0 010 18M12 3a15.3 15.3 0 000 18M12 3a9 9 0 100 18 9 9 0 000-18z" />
									</svg>
								</div>
								<div>
									<p class="text-[13px] font-bold text-dark-700 dark:text-white">
										Public
									</p>
									<p
										class="text-[12px] text-muted dark:text-muted-light leading-relaxed mt-0.5">
										Anyone can find and book this event instantly. Tickets are
										confirmed automatically, no approval needed.
									</p>
								</div>
							</div>
						</button>

						<!-- Private option -->
						<button
							type="button"
							@click="event.is_private = true"
							class="text-left p-4 rounded-xl border-2 transition-all duration-200"
							:class="
								event.is_private
									? 'border-primary-400 bg-primary-50 dark:bg-primary-400/10'
									: 'border-primary-100 dark:border-dark-500 bg-primary-50/40 dark:bg-dark-600/40 hover:border-primary-300'
							">
							<div class="flex items-start gap-3">
								<div
									class="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
									:class="
										event.is_private
											? 'bg-primary-400 text-white'
											: 'bg-primary-100 dark:bg-dark-500 text-primary-400'
									">
									<svg
										class="w-4.5 h-4.5"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24">
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
									</svg>
								</div>
								<div>
									<p class="text-[13px] font-bold text-dark-700 dark:text-white">
										Private
									</p>
									<p
										class="text-[12px] text-muted dark:text-muted-light leading-relaxed mt-0.5">
										Users can request to book, but you (the organizer) must
										approve each attendee before their spot is confirmed.
									</p>
								</div>
							</div>
						</button>
					</div>

					<!-- actions -->
					<div class="flex gap-3">
						<button
							@click="closeVisibilityModal"
							class="flex-1 px-4 py-2.5 rounded-xl text-[13px] font-semibold bg-primary-50 dark:bg-dark-600 text-dark-400 dark:text-white/70 border border-primary-100 dark:border-dark-500 hover:bg-primary-100 dark:hover:bg-dark-500 transition-colors">
							Cancel
						</button>
						<button
							@click="confirmVisibilityAndSubmit"
							:disabled="isSubmitting"
							class="flex-1 px-4 py-2.5 rounded-xl text-[13px] font-bold text-center bg-dark-700 dark:bg-primary-400 text-white hover:bg-primary-600 dark:hover:bg-primary-600 hover:-translate-y-0.5 shadow-card transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none">
							{{ isSubmitting ? 'Creating...' : 'Confirm & Save Event' }}
						</button>
					</div>
				</div>
			</div>
		</div>
	</transition>
</template>

<script>
import { reactive, ref, onMounted, onUnmounted, watch, computed } from "vue";
import { LMap, LTileLayer, LMarker } from "@vue-leaflet/vue-leaflet";
import "leaflet/dist/leaflet.css";
import { apiDataService } from "@/services/apiDataService";
import "vue-toast-notification/dist/theme-sugar.css";
import axios from "axios";
import ErrorMessage from "@/components/messages/ErrorMessage.vue";
import { useStore } from "vuex";
import { showToast } from "@/constants/toast";
import { getErrorMessage } from "@/utils/errorHandler";
import { useRouter } from "vue-router";
import Breadcrumbs from "@/components/Breadcrumbs.vue";
import ImageCropperModal from "@/components/ImageCropperModal.vue";

export default {
	components: { LMap, LTileLayer, LMarker, ErrorMessage, Breadcrumbs, ImageCropperModal },

	setup() {
		const store = useStore();
		const isSubmitting = ref(false);
		const router = useRouter();
		const event = reactive({
			title: "",
			lat: 51.505,
			lng: -0.09,
			date: "",
			endDate: "",
			startTime: "",
			endTime: "",
			image: null,
			imageURL: "",
			organizer: "",
			capacity: 0,
			price: "",
			category: "",
			prices: [{ type: "", amount: "" }],
			selectedCategories: [],
			addOtherOrganizers: false,
			organizerLogos: [],
			organizerLogoURLs: [],
			description: "",
			locationName: "",
			// ── Visibility (public / private) ──
			is_private: false,
		});
		// ── Image cropper ──
		const cropperOpen = ref(false);
		const cropperSrc = ref("");
		const cropperFileName = ref("");

		const errors = reactive({
			title: "",
			location: "",
			date: "",
			endDate: "",
			startTime: "",
			endTime: "",
			price: "",
			capacity: "",
			organizer: "",
			image: "",
			categories: "",
			description: "",
			organizerLogos: [],
		});

		const fileInput = ref(null);
		const selectedFiles = ref([]);
		const userInfo = computed(() => store.state.auth.user || {});
		console.log(userInfo.value);

		const organizations = computed(() => store.state.organizer.organizers);
		const getOrganizers = () => store.dispatch("organizer/fetchAllOrganizers");
		const forAdmin = () => {
			const type = Number(userInfo.value.user_type);
			return type !== 1 && type !== 2;
		};
		const forClient = () => {
			const type = Number(userInfo.value.user_type);
			return type !== 1 && type === 2;
		};

		// ── Stripe gate ──────────────────────────────────────────
		const stripeConnected = ref(false);
		const showStripeModal = ref(false);

		const checkStripeStatus = async () => {
			try {
				const res = await apiDataService.stripeStatus();
				const data = res.data;
				stripeConnected.value =
					data.onboarding_status === "complete" &&
					data.payouts_enabled === true;
			} catch {
				stripeConnected.value = false;
			}
		};

		const resetPriceAmounts = () => {
			event.prices = event.prices.map((p) => ({ ...p, amount: "" }));
			showStripeModal.value = false;
		};

		const handlePriceAmountInput = (price, rawValue) => {
			const num = parseFloat(rawValue);
			if (num > 0 && !stripeConnected.value) {
				price.amount = "";
				showStripeModal.value = true;
				return;
			}
			price.amount = rawValue;
		};
		// ─────────────────────────────────────────────────────────

		// ── Visibility gate (public / private) ─────────────────
		const showVisibilityModal = ref(false);

		const openVisibilityModal = () => {
			showVisibilityModal.value = true;
		};
		const closeVisibilityModal = () => {
			showVisibilityModal.value = false;
		};
		const confirmVisibilityAndSubmit = async () => {
			await submitEvent();
			if (!isSubmitting.value) {
				// isSubmitting is reset to false in submitEvent's finally block
				showVisibilityModal.value = false;
			}
		};
		// ─────────────────────────────────────────────────────────

		const locationQuery = ref("");
		const locationSuggestions = ref([]);
		const zoom = ref(12);
		let timeout;

		const fetchLocationSuggestions = () => {
			if (locationQuery.value.length < 3) return;
			clearTimeout(timeout);
			timeout = setTimeout(async () => {
				const res = await axios.get(
					"https://nominatim.openstreetmap.org/search",
					{
						params: { q: locationQuery.value, format: "json", limit: 5 },
					},
				);
				locationSuggestions.value = res.data;
			}, 300);
		};

		const selectLocation = async (suggestion) => {
			event.lat = parseFloat(suggestion.lat);
			event.lng = parseFloat(suggestion.lon);
			locationQuery.value = suggestion.display_name;
			locationSuggestions.value = [];
			await getLocationNameFromCoordinates(event.lat, event.lng);
		};

		const setLocation = async (e) => {
			event.lat = e.latlng.lat;
			event.lng = e.latlng.lng;
			await getLocationNameFromCoordinates(event.lat, event.lng);
		};

		const getLocationNameFromCoordinates = async (lat, lng) => {
			try {
				const res = await axios.get(
					"https://nominatim.openstreetmap.org/reverse",
					{
						params: { lat, lon: lng, format: "json", addressdetails: 1 },
					},
				);
				event.locationName = res.data.display_name;
			} catch (err) {
				console.error("Error fetching location name:", err);
			}
		};

		const validateTitle = () => {
			errors.title = event.title.trim() ? "" : "Title is required";
		};
		const validateLocation = () => {
			errors.location =
				event.lat && event.lng ? "" : "Please select a location";
		};
		const MIN_LEAD_TIME_MINUTES = 60;
		const MIN_DURATION_MINUTES = 30;
		const getStartDateTime = () => {
			if (!event.date || !event.startTime) return null;
			return new Date(`${event.date}T${event.startTime}`);
		};
		const getEndDateTime = () => {
			if (!event.endDate || !event.endTime) return null;
			return new Date(`${event.endDate}T${event.endTime}`);
		};

		const validateDate = () => {
			if (!event.date) {
				errors.date = "Date is required";
				return;
			}
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			const selected = new Date(event.date);
			selected.setHours(0, 0, 0, 0);
			errors.date = selected < today ? "Date cannot be in the past" : "";
		};

		const validateEndDate = () => {
			if (!event.endDate) {
				errors.endDate = "End date is required";
				return;
			}
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			const endDay = new Date(event.endDate);
			endDay.setHours(0, 0, 0, 0);
			if (endDay < today) {
				errors.endDate = "End date cannot be in the past";
				return;
			}
			if (event.date) {
				const startDay = new Date(event.date);
				startDay.setHours(0, 0, 0, 0);
				if (endDay < startDay) {
					errors.endDate = "End date cannot be before start date";
					return;
				}
			}
			errors.endDate = "";
		};

		const validateStartTime = () => {
			if (!event.date || !event.startTime) {
				errors.startTime = "Start date and time are required";
				return;
			}
			const startDateTime = getStartDateTime();
			if (!startDateTime || isNaN(startDateTime.getTime())) {
				errors.startTime = "Invalid start date/time";
				return;
			}
			const now = new Date();
			const diffMs = startDateTime.getTime() - now.getTime();
			const minLeadMs = MIN_LEAD_TIME_MINUTES * 60 * 1000;
			if (diffMs < 0)
				errors.startTime = "Start date and time cannot be in the past";
			else if (diffMs < minLeadMs)
				errors.startTime = `Start time must be at least ${MIN_LEAD_TIME_MINUTES} minutes from now`;
			else errors.startTime = "";
		};

		const validateEndTime = () => {
			const startDateTime = getStartDateTime();
			const endDateTime = getEndDateTime();
			const now = new Date();
			if (!event.endDate || !event.endTime) {
				errors.endTime = "End date and time are required";
				return;
			}
			if (!startDateTime || isNaN(startDateTime.getTime())) {
				errors.endTime = "Please choose a valid start date and time first";
				return;
			}
			if (!endDateTime || isNaN(endDateTime.getTime())) {
				errors.endTime = "Invalid end date/time";
				return;
			}
			if (endDateTime < now) {
				errors.endTime = "End time cannot be in the past";
				return;
			}
			if (endDateTime <= startDateTime) {
				errors.endTime = "End time must be after start time";
				return;
			}
			const durationMs = endDateTime.getTime() - startDateTime.getTime();
			if (durationMs < MIN_DURATION_MINUTES * 60 * 1000) {
				errors.endTime = `Event duration must be at least ${MIN_DURATION_MINUTES} minutes`;
				return;
			}
			errors.endTime = "";
		};

		const validatePrice = () => {
			const hasValid = event.prices.some(
				(p) => p.type.trim() !== "" && parseFloat(p.amount) >= 0,
			);
			errors.price = hasValid
				? ""
				: "At least one price type and amount must be filled.";
			return hasValid;
		};
		const validateCapacity = () => {
			errors.capacity =
				event.capacity > 0 ? "" : "Capacity must be a positive number";
		};
		const validateOrganizer = () => {
			errors.organizer =
				event.organizer !== null && event.organizer !== undefined
					? ""
					: "Organizer is required";
		};
		const validateCategories = () => {
			errors.categories = event.selectedCategories.length
				? ""
				: "Please select at least one category";
		};
		const validateDescription = () => {
			errors.description = event.description.length
				? ""
				: "You must write something that describe your event";
		};

		const validateForm = () => {
			validateTitle();
			validateLocation();
			validateDate();
			validateEndDate();
			validateStartTime();
			validateEndTime();
			validatePrice();
			validateCapacity();
			validateOrganizer();
			validateCategories();
			validateDescription();
			const mainErrors = {
				title: errors.title,
				location: errors.location,
				date: errors.date,
				endDate: errors.endDate,
				startTime: errors.startTime,
				endTime: errors.endTime,
				price: errors.price,
				capacity: errors.capacity,
				organizer: errors.organizer,
				categories: errors.categories,
				description: errors.description,
			};
			return Object.values(mainErrors).every((e) => !e);
		};

		watch(() => event.title, validateTitle);
		watch(
			() => event.date,
			() => {
				validateDate();
				validateStartTime();
				validateEndTime();
			},
		);
		watch(
			() => event.startTime,
			() => {
				validateStartTime();
				validateEndTime();
			},
		);
		watch(
			() => event.endDate,
			() => {
				validateEndDate();
				validateEndTime();
			},
		);
		watch(() => event.endTime, validateEndTime);
		watch(() => event.capacity, validateCapacity);
		watch(() => event.organizer, validateOrganizer);
		watch(() => event.selectedCategories, validateCategories);
		watch(() => event.prices, validatePrice, { deep: true });

		const isValidForm = computed(() => validateForm());
		const isLoading = computed(() => store.state.event.loading);
		const eventWords = ref({ description: "" });
		const descriptionError = ref({ description: "" });
		const charCount = ref(0);
		const minChars = 100;
		const maxChars = 500;
		const isValid = ref(true);
		const exceedMaxChar = ref("");
		const minimumChar = ref("");

		const setMessage = (type, text) => {
			exceedMaxChar.value = "";
			minimumChar.value = "";
			if (type === "ExceedMaxChar") exceedMaxChar.value = text;
			if (type === "MinimumChar") minimumChar.value = text;
		};

		const handleTextarea = (e) => {
			e.target.style.height = "auto";
			e.target.style.height = e.target.scrollHeight + "px";
			if (e.target.value.length > maxChars)
				setMessage(
					"ExceedMaxChar",
					`❌ Description cannot exceed 500 characters.`,
				);
			eventWords.value.description = e.target.value;
			charCount.value = e.target.value.length;
			if (charCount.value < minChars && charCount.value < maxChars) {
				isValid.value = false;
				setMessage(
					"MinimumChar",
					`⚠️ Description must be at least 100 - 500 characters.`,
				);
			} else {
				isValid.value = true;
			}
		};

		const categories = computed(() => store.state.category.categories);
		const selectedCategory = ref("");
		const getCategories = () => store.dispatch("category/fetchAllCategories");

		const addCategory = () => {
			if (
				selectedCategory.value &&
				!event.selectedCategories.includes(selectedCategory.value)
			) {
				if (event.selectedCategories.length < 3)
					event.selectedCategories.push(selectedCategory.value);
				else alert("You can select no more than 3 categories.");
			}
			selectedCategory.value = "";
		};

		const removeCategory = (id) => {
			event.selectedCategories = event.selectedCategories.filter(
				(cat) => cat !== id,
			);
		};
		const getCategoryName = (id) => {
			const cat = categories.value.find((c) => c.id.toString() === id);
			return cat ? cat.name : "Unknown Category";
		};

		const addPrice = () => {
			const last = event.prices[event.prices.length - 1];
			if (!last || !last.type || last.amount === "") {
				errors.price =
					"Please fill the current price fields before adding a new one.";
				return;
			}
			event.prices.push({ type: "", amount: "" });
		};
		const removePrice = (index) => {
			event.prices.splice(index, 1);
		};

		const browseFiles = () => {
			if (fileInput.value) fileInput.value.click();
			else console.warn("File input is not available in the DOM yet.");
		};
		const handleFileChange = (event) => {
			selectedFiles.value = Array.from(event.target.files);
		};
		const handleDrop = (event) => {
			const file = event.dataTransfer.files[0];
			if (!file) return;
			handleImageUpload({ target: { files: [file] } });
		};
		const cancelUpload = () => {
			selectedFiles.value = [];
			fileInput.value.value = null;
		};
		const closeCropper = () => {
			if (cropperSrc.value) URL.revokeObjectURL(cropperSrc.value);
			cropperSrc.value = "";
			cropperOpen.value = false;
			if (fileInput.value) fileInput.value.value = null; // lets the same file be picked again
		};

		// Now only validates, then opens the cropper (no longer sets the image directly)
		const handleImageUpload = (e) => {
			const file = e.target.files?.[0];
			if (!file) return; // user cancelled the file dialog
			if (!file.type.startsWith("image/")) {
				alert("Please upload an image file.");
				return;
			}
			if (file.size > 5 * 1024 * 1024) {
				alert("File size exceeds 5MB.");
				return;
			}
			cropperFileName.value = file.name;
			cropperSrc.value = URL.createObjectURL(file);
			cropperOpen.value = true;
		};
		// Cropper returns the cropped File; this is where the image is actually set
		const onCropDone = (file) => {
			if (event.imageURL) URL.revokeObjectURL(event.imageURL);
			event.image = file;                         // used by formData.append("image", event.image)
			event.imageURL = URL.createObjectURL(file); // used by the preview <img>
			closeCropper();
		};
		const resetImage = () => {
			if (event.imageURL) URL.revokeObjectURL(event.imageURL);
			event.image = null;
			event.imageURL = "";
			selectedFiles.value = [];
			if (fileInput.value) fileInput.value.value = null;
		};
		const handleLogoUpload = (index, e) => {
			const file = e.target.files[0];
			if (!file.type.startsWith("image/")) {
				errors.organizerLogos[index] = "Invalid image file.";
				return;
			}
			if (file.size > 5 * 1024 * 1024) {
				errors.organizerLogos[index] = "File too large.";
				return;
			}
			while (event.organizerLogos.length <= index) {
				event.organizerLogos.push(null);
				event.organizerLogoURLs.push("");
			}
			event.organizerLogos[index] = file;
			event.organizerLogoURLs[index] = URL.createObjectURL(file);
			errors.organizerLogos[index] = "";
		};
		const addOrganizerLogo = () => {
			if (event.organizerLogos.filter(Boolean).length < 3) {
				event.organizerLogos.push(null);
				event.organizerLogoURLs.push("");
			}
		};
		const removeOrganizerLogo = (index) => {
			if (event.organizerLogoURLs[index])
				URL.revokeObjectURL(event.organizerLogoURLs[index]);
			event.organizerLogos.splice(index, 1);
			event.organizerLogoURLs.splice(index, 1);
		};
		onUnmounted(() => {
			event.organizerLogoURLs.forEach((url) => URL.revokeObjectURL(url));
		});

		// ── Called on form submit: validates, then opens the
		//    visibility modal instead of hitting the API directly ──
		const submitForm = async () => {
			if (!validateForm()) {
				console.log("Form invalid");
				return;
			}
			openVisibilityModal();
		};

		// ── Actual API call, now carries the chosen visibility ──
		const submitEvent = async () => {
			isSubmitting.value = true;
			const formData = new FormData();
			formData.append("title", event.title);
			formData.append("latitude", event.lat.toString());
			formData.append("longitude", event.lng.toString());
			formData.append("location_name", event.locationName);
			formData.append("date", event.date);
			formData.append("end_date", event.endDate);
			formData.append("start_time", event.startTime);
			formData.append("end_time", event.endTime);
			formData.append("organizer", event.organizer);
			formData.append("capacity", event.capacity.toString());
			formData.append("price", event.price);
			formData.append("category", event.category);
			formData.append("description", event.description);
			// backend field: "is_private" -> boolean (true = private, false = public)
			formData.append("is_private", event.is_private.toString());
			event.prices.forEach((p, i) => {
				formData.append(`price_type_${i}_type`, p.type);
				formData.append(`price_type_${i}_amount`, p.amount);
			});
			event.selectedCategories.forEach((c, i) => {
				formData.append(`category_${i}`, c);
			});
			if (event.image) formData.append("image", event.image);
			if (event.addOtherOrganizers)
				event.organizerLogos.forEach((logo, i) => {
					if (logo) formData.append(`organizer_logo_${i}`, logo);
				});
			try {
				const res = await apiDataService.createEvent(formData);
				if (res.status === 201) {
					console.log("Success", res.data);
					showToast("success", res.data.message || "Event created successfully!");
					showVisibilityModal.value = false;
					router.push({ name: "eventList" });
				}
			} catch (err) {
				const message = getErrorMessage(err);
				showToast("error", message);
				console.error("Submission error:", err);
			} finally {
				isSubmitting.value = false;
			}
		};

		const createEvent = async () => {
			isLoading.value = true;
			try {
				const response = await apiDataService.createEvent(event);
				console.log(response.data);
				showToast("success", `${response.data.Name}created successfully!`);
				event.name = "";
				event.price = "";
			} catch (error) {
				console.error("Error saving product:", error);
				const message = getErrorMessage(error);
				showToast("error", message);
			} finally {
				isLoading.value = false;
			}
		};

		onMounted(() => {
			if (!categories.value.length) getCategories();
			if (!organizations.value.length) getOrganizers();
			if (forClient()) event.organizer = userInfo.value.organization_id;
			checkStripeStatus(); // ← Stripe gate check
		});

		return {
			event,
			createEvent,
			isLoading,
			addCategory,
			removeCategory,
			getCategoryName,
			categories,
			selectedCategory,
			addPrice,
			removePrice,
			handleImageUpload,
			resetImage,
			handleLogoUpload,
			addOrganizerLogo,
			removeOrganizerLogo,
			browseFiles,
			handleDrop,
			cancelUpload,
			handleFileChange,
			fileInput,
			isSubmitting,
			errors,
			locationQuery,
			locationSuggestions,
			fetchLocationSuggestions,
			selectLocation,
			setLocation,
			zoom,
			validateForm,
			validateTitle,
			validateLocation,
			validateDate,
			validateStartTime,
			validateEndTime,
			validatePrice,
			validateCapacity,
			validateOrganizer,
			validateCategories,
			validateEndDate,
			isValidForm,
			submitForm,
			userInfo,
			forAdmin,
			forClient,
			organizations,
			handleTextarea,
			charCount,
			maxChars,
			descriptionError,
			isValid,
			minChars,
			setMessage,
			exceedMaxChar,
			minimumChar,
			// ── Stripe gate ──
			stripeConnected,
			showStripeModal,
			handlePriceAmountInput,
			resetPriceAmounts,
			// ── Visibility gate ──
			showVisibilityModal,
			openVisibilityModal,
			closeVisibilityModal,
			confirmVisibilityAndSubmit,
			submitEvent,
			// ── Image cropper ──
			cropperOpen,
			cropperSrc,
			cropperFileName,
			closeCropper,
			onCropDone,
		};
	},
};
</script>

<style scoped>
/* ── Reusable form primitives ── */
.form-label {
	@apply flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest mb-1
         text-muted dark:text-muted-light;
}

.form-input {
	@apply w-full px-3.5 py-2.5 rounded-xl text-[13px] font-medium
         bg-primary-50/60 dark:bg-dark-600
         border border-primary-100 dark:border-dark-500
         text-dark-700 dark:text-white
         placeholder-muted dark:placeholder-muted-light
         outline-none transition-all duration-200
         focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20
         dark:focus:border-primary-400;
}

/* Select arrow colour fix in dark mode */
select.form-input {
	@apply cursor-pointer;
}

.form-divider {
	@apply flex items-center gap-3 my-1;
}

.form-divider::before,
.form-divider::after {
	content: "";
	@apply flex-1 border-t border-dashed border-primary-100 dark:border-dark-600;
}

/* ── Modal transition ── */
.modal-enter-active,
.modal-leave-active {
	transition: opacity 0.2s ease;
}
.modal-enter-from,
.modal-leave-to {
	opacity: 0;
}
</style>